/**
 * Precision Policy Management — Main container.
 * No internal sidebar — Core's AppSidebar handles app navigation.
 * PPM uses a header with horizontal tab navigation.
 *
 * Five tabs, one job each:
 *   Library  — the system of record
 *   Inbox    — the single action queue (recommendations awaiting decisions)
 *   Radar    — everything the scanners found (conflicts, gaps, outdated), evidence views
 *   Mandates — the regulatory reference library
 *   Admin    — audit trail + field configuration
 * Formal sign-off lives in the dedicated SignoffHub app; the header button
 * deep-links there instead of duplicating it as a tab.
 */

import React, { useState, useEffect, useRef } from 'react';
import PolicyLibrary from './PolicyLibrary';
import { takePolicyParam } from './policyParam.js';
import PolicyDetail from './PolicyDetail';
import PPMInbox from './PPMInbox';
import RecommendationDetail from './RecommendationDetail';
import MandateBrowser from './MandateBrowser';
import RadarView from './RadarView';
import AuditTrail from './AuditTrail';
import OrgFieldConfig from './OrgFieldConfig';
import AnnualReviewView from './AnnualReviewView';
import { PolicyReviewSection } from '../Workspace';
import {
  BookOpenIcon,
  InboxIcon,
  ClipboardDocumentCheckIcon,
  PaperAirplaneIcon,
  SignalIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const TABS = [
  { id: 'reviews',  label: 'Your reviews',   Icon: ClipboardDocumentCheckIcon, tip: 'Policies waiting on you: confirm each one as current, upload a revised document, or hand it to a teammate, then attest. The same list the review email opens.' },
  { id: 'library',  label: 'Policy Library', Icon: BookOpenIcon, tip: "Browse, search, upload, and create your organization's policies — your system of record." },
  { id: 'inbox',    label: 'Inbox',          Icon: InboxIcon,    tip: 'The single action queue: recommendations waiting for your review and decision.' },
  { id: 'radar',    label: 'Radar',          Icon: SignalIcon,   tip: 'What the scanners found: policies that contradict each other, regulatory requirements with no covering policy, and policies whose regulation changed. Anything needing action also lands in the Inbox.' },
  { id: 'mandates', label: 'Mandates',       Icon: ScaleIcon,    tip: 'The reference library of regulatory requirements — the CFR sections your policies must satisfy. This is what Radar measures against.' },
  { id: 'annual',   label: 'Annual Review',  Icon: ArrowPathIcon, tip: 'Launch the annual review: every policy owner is emailed their assignments, completes them from their home page with an attestation, and you approve each one before it publishes. Every email sent and every reply is kept under Correspondence.' },
  { id: 'admin',    label: 'Admin',          Icon: ShieldCheckIcon, tip: 'The time-stamped audit trail and the custom-field configuration.' },
];

function AdminView() {
  const [section, setSection] = useState('audit');
  return (
    <div className="min-h-full">
      <div className="px-8 pt-6">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1">
          {[['audit', 'Audit Trail'], ['config', 'Field Config']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setSection(id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                section === id ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {section === 'audit' ? <AuditTrail /> : <OrgFieldConfig />}
    </div>
  );
}

export default function PPMCommandCenter() {
  const [view, setView] = useState('library');
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [selectedRecommendationId, setSelectedRecommendationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Policies waiting on this user. When there are any, the app opens on them,
  // the way the review email does; the launcher badge shows the same number.
  const [reviewCount, setReviewCount] = useState(null);
  const userChoseTab = useRef(false);
  // A link into one policy (Readiness "Open the policy", and anything else
  // that sends ?app=ppm&policy=<id>) lands on that policy, not the library.
  // The parameter is consumed so Back never re-opens it.
  useEffect(() => {
    const id = takePolicyParam();
    if (!id) return;
    userChoseTab.current = true;
    setSelectedPolicyId(id); setView('policy-detail');
  }, []);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/ppm-review/my`, { credentials: 'include' });
        const d = await res.json();
        if (cancelled || !d?.success) return;
        const n = (d.tasks || []).length;
        setReviewCount(n);
        // Open on the waiting work until the user picks a tab themselves.
        if (n > 0 && !userChoseTab.current) setView((v) => (v === 'library' ? 'reviews' : v));
      } catch { /* the tab still works without the count */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/api/ppm/inbox/count`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setUnreadCount(data.count);
      } catch {}
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, []);

  const handleOpenPolicy = (id) => { setSelectedPolicyId(id); setView('policy-detail'); };
  const handleOpenRecommendation = (id) => { setSelectedRecommendationId(id); setView('recommendation-detail'); };
  const handleBack = () => {
    if (view === 'recommendation-detail') { setSelectedRecommendationId(null); setView('inbox'); }
    else { setSelectedPolicyId(null); setView('library'); }
  };

  const handleTabSelect = (tabId) => {
    userChoseTab.current = true;
    setView(tabId);
    setSelectedPolicyId(null);
    setSelectedRecommendationId(null);
  };

  const openSignoffHub = () => {
    // Formal ratification lives in the dedicated SignoffHub app. Deep-link
    // there (same pattern as COA's "Send to SignoffHub").
    window.location.assign('/app?app=ratify');
  };

  const isDetailView = view === 'policy-detail' || view === 'recommendation-detail';

  const renderContent = () => {
    if (view === 'policy-detail' && selectedPolicyId) return <PolicyDetail policyId={selectedPolicyId} onBack={handleBack} />;
    if (view === 'recommendation-detail' && selectedRecommendationId) return <RecommendationDetail recommendationId={selectedRecommendationId} onBack={handleBack} onOpenPolicy={handleOpenPolicy} />;
    if (view === 'inbox') return <PPMInbox onOpenRecommendation={handleOpenRecommendation} onOpenPolicy={handleOpenPolicy} />;
    if (view === 'radar') return <RadarView onOpenPolicy={handleOpenPolicy} />;
    if (view === 'mandates') return <MandateBrowser onOpenPolicy={handleOpenPolicy} />;
    if (view === 'annual') return <AnnualReviewView />;
    if (view === 'reviews') return (
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-8 pb-16">
          {reviewCount === 0 && (
            <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center">
              <div className="text-base font-semibold text-slate-800">Nothing is waiting on you</div>
              <p className="mt-1 text-sm text-slate-500">When a policy you own comes up for its annual review, it appears here and the Policy Management tile in the launcher shows the count.</p>
            </div>
          )}
          <PolicyReviewSection />
        </div>
      </div>
    );
    if (view === 'admin') return <AdminView />;
    return <PolicyLibrary onOpenPolicy={handleOpenPolicy} />;
  };

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] min-w-0 min-h-0">
      {/* Header */}
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60">
        {/* Top bar */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">Precision Policy Management</h1>
              <p className="text-slate-400 text-sm mt-0.5">Enterprise policy system of record · Regulatory intelligence hub</p>
            </div>
            <button
              type="button"
              onClick={openSignoffHub}
              title="Send policies for formal sign-off by committees, executives, or the board — opens the SignoffHub app."
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-600 text-white font-medium text-sm hover:bg-orange-700 shadow-sm transition-all flex-shrink-0"
            >
              <PaperAirplaneIcon className="w-4 h-4" strokeWidth={2} />
              Send to SignoffHub
            </button>
          </div>
        </div>

        {/* Tab navigation */}
        {!isDetailView && (
          <div className="px-8 pb-0">
            <div className="flex gap-1 border-b-0">
              {TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabSelect(tab.id)}
                  title={tab.tip}
                  className={`relative inline-flex items-center px-5 py-3 text-sm font-semibold transition-all rounded-t-xl ${
                    view === tab.id
                      ? 'bg-[#f8fafc] text-orange-700 border border-slate-200/60 border-b-[#f8fafc] -mb-px z-10'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <tab.Icon className="w-4 h-4 mr-1.5 flex-shrink-0" strokeWidth={1.75} />
                  {tab.label}
                  {tab.id === 'reviews' && reviewCount > 0 && (
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      view === 'reviews' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-600'
                    }`}>{reviewCount}</span>
                  )}
                  {tab.id === 'inbox' && unreadCount > 0 && (
                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      view === 'inbox' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-600'
                    }`}>{unreadCount}</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        {renderContent()}
      </main>
    </div>
  );
}
